import { Database } from "../../supabase.types";
import { createSupabaseAdminClient } from "../../supabase/supabase";
import { logSupabaseException } from "@punchup/sentry";

const TABLE = "comedian_representation_contacts";

export type ComedianRepresentationContactRow =
  Database["public"]["Tables"]["comedian_representation_contacts"]["Row"];

/**
 * The manager-supplied fields of a representation contact. `email`/`phone` are
 * nullable (at least one is required — enforced by the route validation and a DB
 * CHECK), `organizationName` is the optional agency / management-company name.
 */
export interface ComedianRepresentationContactFields {
  contactType: string;
  contactName: string;
  organizationName: string | null;
  email: string | null;
  phone: string | null;
  sortOrder?: number;
}

export interface CreateComedianRepresentationContactInput extends ComedianRepresentationContactFields {
  comedianId: string;
}

/**
 * All representation-contact fetchers use the service-role client, so writes are
 * NOT subject to RLS. Authorization is the CALLER's responsibility — the v2
 * manager routes verify the user manages the target comedian (via
 * `getIsUserAuthorizedToManageComedian`) before invoking these.
 */

/** Lists a comedian's representation contacts in display order. */
export async function listComedianRepresentationContacts(
  comedianId: string
): Promise<ComedianRepresentationContactRow[]> {
  const supabase = createSupabaseAdminClient();
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("comedian_id", comedianId)
    .order("sort_order", { ascending: true });

  if (error) {
    logSupabaseException(
      error,
      "Failed to list comedian representation contacts"
    );
    throw error;
  }
  return data ?? [];
}

/** Loads a single contact by id (used to resolve its comedian for authorization). */
export async function getComedianRepresentationContactById(
  contactId: string
): Promise<ComedianRepresentationContactRow | null> {
  const supabase = createSupabaseAdminClient();
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("id", contactId)
    .maybeSingle();

  if (error) {
    logSupabaseException(
      error,
      "Failed to load comedian representation contact"
    );
    throw error;
  }
  return data ?? null;
}

/** Creates a representation contact for a comedian. */
export async function createComedianRepresentationContact(
  input: CreateComedianRepresentationContactInput
): Promise<ComedianRepresentationContactRow> {
  const supabase = createSupabaseAdminClient();
  const { data, error } = await supabase
    .from(TABLE)
    .insert({
      comedian_id: input.comedianId,
      contact_type: input.contactType,
      organization_name: input.organizationName,
      contact_name: input.contactName,
      email: input.email,
      phone: input.phone,
      ...(input.sortOrder !== undefined ? { sort_order: input.sortOrder } : {}),
    })
    .select("*")
    .single();

  if (error) {
    logSupabaseException(
      error,
      "Failed to create comedian representation contact"
    );
    throw error;
  }
  if (!data) {
    throw new Error("createComedianRepresentationContact: no row returned");
  }
  return data;
}

/** Updates a representation contact's fields by id. */
export async function updateComedianRepresentationContact(
  contactId: string,
  fields: ComedianRepresentationContactFields
): Promise<ComedianRepresentationContactRow> {
  const supabase = createSupabaseAdminClient();
  const { data, error } = await supabase
    .from(TABLE)
    .update({
      contact_type: fields.contactType,
      organization_name: fields.organizationName,
      contact_name: fields.contactName,
      email: fields.email,
      phone: fields.phone,
      ...(fields.sortOrder !== undefined
        ? { sort_order: fields.sortOrder }
        : {}),
    })
    .eq("id", contactId)
    .select("*")
    .single();

  if (error) {
    logSupabaseException(
      error,
      "Failed to update comedian representation contact"
    );
    throw error;
  }
  if (!data) {
    throw new Error("updateComedianRepresentationContact: no row returned");
  }
  return data;
}

/**
 * Persists a new display order for a comedian's contacts (INB-51 drag-reorder).
 * `orderedContactIds` is the comedian's contact ids in the desired order; each row's
 * `sort_order` is set to its index. Every id is verified to belong to `comedianId`
 * first (a manager cannot renumber another comedian's rows). Per-row updates scoped
 * to `comedian_id` — not a transaction, but the lists are tiny; a partial failure
 * throws. Service-role client; the route authorizes the caller.
 */
export async function reorderComedianRepresentationContacts(
  comedianId: string,
  orderedContactIds: string[]
): Promise<void> {
  const supabase = createSupabaseAdminClient();

  const { data: owned, error: ownedError } = await supabase
    .from(TABLE)
    .select("*")
    .eq("comedian_id", comedianId);

  if (ownedError) {
    logSupabaseException(ownedError, "Failed to load contacts for reorder");
    throw ownedError;
  }

  const ownedRows = owned ?? [];
  const ownedIds = new Set(ownedRows.map((r) => r.id));
  const allBelong = orderedContactIds.every((id) => ownedIds.has(id));
  if (!allBelong) {
    throw new Error(
      "reorderComedianRepresentationContacts: all contactIds must belong to the comedian"
    );
  }

  // Apply the new positions in ONE atomic upsert (INSERT … ON CONFLICT DO UPDATE)
  // rather than N separate updates, so a mid-batch failure can't leave a
  // half-applied order committed. Rows not in the ordered list keep their position.
  const orderIndexById = new Map(
    orderedContactIds.map((id, index) => [id, index] as const)
  );
  const reordered = ownedRows.map((row) => ({
    ...row,
    sort_order: orderIndexById.get(row.id) ?? row.sort_order,
  }));

  const { error: upsertError } = await supabase
    .from(TABLE)
    .upsert(reordered, { onConflict: "id" });

  if (upsertError) {
    logSupabaseException(
      upsertError,
      "Failed to reorder comedian representation contacts"
    );
    throw upsertError;
  }
}

/** Deletes a representation contact by id (hard delete). */
export async function deleteComedianRepresentationContact(
  contactId: string
): Promise<void> {
  const supabase = createSupabaseAdminClient();
  const { error } = await supabase.from(TABLE).delete().eq("id", contactId);

  if (error) {
    logSupabaseException(
      error,
      "Failed to delete comedian representation contact"
    );
    throw error;
  }
}
